import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password);
      
      if (user.status === "pending") {
        toast.info("Tu cuenta está pendiente de aprobación");
      } else if (user.status === "rejected") {
        toast.error("Tu cuenta ha sido rechazada. Contactanos por WhatsApp.");
      } else {
        toast.success(`¡Bienvenido, ${user.name}!`);
      }
      
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl md:text-3xl mb-2">INICIAR SESIÓN</h1>
          <p className="text-muted-foreground">
            Ingresá con tu email y contraseña
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-6 md:p-8 space-y-5">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              required
              data-testid="login-email"
            />
          </div>

          {/* Contraseña */}
          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Contraseña
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Tu contraseña"
              required
              data-testid="login-password"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 text-base"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? "Ingresando..." : "Ingresar"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            ¿No tenés cuenta?{" "}
            <Link to="/registro" className="text-primary hover:underline font-medium">
              Registrate
            </Link>
          </p>
        </form>

        {/* Info box */}
        <div className="mt-6 bg-muted/50 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium mb-1">¿Problemas para ingresar?</p>
            <p className="text-muted-foreground">
              Contactanos por WhatsApp y te ayudamos.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
